import shopify from "../../assets/shopify_glyph.svg";
import { Button, buttonVariants } from "@/components/ui/button";
import { ErrorText } from "@/components/ui/error";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  loginShopifyPath,
  signupPath,
  verifyPath,
  VITE_API_URL,
} from "@/constants";
import { cn } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/auth/login")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const resp = await fetch(`${VITE_API_URL}/auth/email`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email,
        }),
      });
      if (resp.status >= 400) {
        throw new Error("failed to authorize");
      }

      nav({ to: verifyPath });
    } catch (e) {
      console.error(e);
      setErr(t("auth.login.invalidCredentials"));
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-full flex flex-col space-y-3">
        <div className="mx-auto w-full max-w-80 relative bottom-8 flex flex-col gap-6">
          <div className="flex w-full flex-col items-center gap-1">
            <h1 className="w-full text-left text-2xl font-semibold tracking-tight text-primary">
              {t("auth.login.title")}
            </h1>
          </div>

          <form method="post" onSubmit={handleSubmit} className="space-y-4">
            {err !== "" && (
              <ErrorText className="w-full text-center">{err}</ErrorText>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-500">
                {t("auth.email")}
              </Label>
              <Input
                type="email"
                name="email"
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                  setErr("");
                }}
                placeholder={t("auth.emailPlaceholder")}
                className="h-10 rounded-sm w-full text-sm font-normal placeholder:text-gray-400"
                required
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              className="flex h-9 items-center justify-center text-white w-full rounded-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.sending") : t("common.continue")}
            </Button>
          </form>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-500" />
            <span className="text-xs font-medium text-gray-400">
              {t("auth.or")}
            </span>
            <div className="h-px flex-1 bg-gray-500" />
          </div>

          <Link
            type="button"
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-9 w-full rounded-sm",
            )}
            to={loginShopifyPath}
          >
            <img src={shopify} alt={t("auth.shopifyLogoAlt")} className="h-5 w-5" />
            {t("auth.login.continueWithShopify")}
          </Link>

          <div className="w-full text-xs font-medium text-slate-600">
            <span>
              {t("auth.login.newHere")}{" "}
              <Link className="text-primary" to={signupPath}>
                {t("auth.login.createAccount")}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
